import React, { useMemo, useState } from "react";
import {
  Scale, MapPin, Home as HomeIcon, BedDouble, Ruler, Trees, Euro,
  Zap, Sparkles, Search, AlertTriangle, ExternalLink, ImageOff, Hourglass,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";

const CATEGORIES = [
  { value: "alle",         label: "Alle types" },
  { value: "huis",         label: "Huis" },
  { value: "appartement",  label: "Appartement" },
  { value: "grond",        label: "Bouwgrond" },
];
const EPC_LABELS = ["A+", "A", "B", "C", "D", "E", "F", "G"];

function epcColor(label) {
  if (!label) return "bg-[var(--sand-100)] text-[var(--ink-700)]";
  const up = label.toUpperCase();
  if (up === "A+" || up === "A") return "bg-[#D7F1DC] text-[#1F6F45]";
  if (up === "B" || up === "C")  return "bg-[#E6F2D6] text-[#3F6B1C]";
  if (up === "D" || up === "E")  return "bg-[#FCEBC9] text-[#7C5A1E]";
  return "bg-[#FBE0DC] text-[#8C2D2D]";
}

function fmtEuro(n) {
  if (n === null || n === undefined || n === "") return "—";
  return new Intl.NumberFormat("nl-BE", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}
function fmtM2(n) {
  if (n === null || n === undefined || n === "") return "—";
  return `${Math.round(n).toLocaleString("nl-BE")} m²`;
}
function fmtRelativeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "vandaag";
  if (days === 1) return "gisteren";
  if (days < 7)  return `${days} dagen geleden`;
  if (days < 30) return `${Math.floor(days / 7)} weken geleden`;
  if (days < 365) return `${Math.floor(days / 30)} maanden geleden`;
  return `${Math.floor(days / 365)} jaar geleden`;
}

export default function Comparison() {
  const [form, setForm] = useState({
    address: "", house_number: "", postal_code: "", city: "",
    price: "", category: "huis", bedrooms: "",
    epc: "", living_area: "", plot_area: "",
    max_distance_km: 8,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const isValid = useMemo(
    () => form.address.trim() && form.postal_code.trim() && form.city.trim(),
    [form],
  );

  const onSearch = async () => {
    setError(null); setLoading(true); setResult(null);
    try {
      const payload = {
        address: form.address.trim(),
        house_number: form.house_number.trim() || null,
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        price: form.price ? Number(form.price) : null,
        category: form.category === "alle" ? null : form.category,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        epc: form.epc || null,
        living_area: form.living_area ? Number(form.living_area) : null,
        plot_area: form.plot_area ? Number(form.plot_area) : null,
        max_distance_km: Number(form.max_distance_km) || 8,
      };
      const r = await http.post("/api/comparison/search", payload);
      if (r.data?.ok === false) {
        setError(r.data.message || "Geen resultaten gevonden");
      } else {
        setResult(r.data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Er ging iets mis bij het zoeken.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 min-w-0 overflow-auto" data-testid="comparison-screen">
      <div className="p-8 max-w-[1280px] mx-auto fade-in">
        <div className="mb-6">
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            {t("nav.goals")}
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            Vergelijkingspunten
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1">
            Vul een referentiepand in en zie welke vergelijkbare panden er binnen 8 km online staan.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ─────────────── Form ─────────────── */}
          <section className="lg:col-span-4">
            <div className="card sticky top-6" data-testid="comparison-form">
              <h2 className="font-display text-[18px] mb-4 text-[var(--ink-900)]
                             flex items-center gap-2">
                <Scale size={17} strokeWidth={1.75} className="text-[var(--teal-700)]" />
                Referentiepand
              </h2>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <FormField label="Straat" icon={MapPin}>
                    <input value={form.address} onChange={set("address")}
                           data-testid="comp-address"
                           placeholder="Pastoor De Smetstraat" className={inputCls} />
                  </FormField>
                </div>
                <FormField label="Nr.">
                  <input value={form.house_number} onChange={set("house_number")}
                         data-testid="comp-house-number"
                         placeholder="12" className={inputCls} />
                </FormField>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Postcode">
                  <input value={form.postal_code} onChange={set("postal_code")}
                         data-testid="comp-postal"
                         placeholder="2830" className={inputCls} maxLength={4} />
                </FormField>
                <div className="col-span-2">
                  <FormField label="Gemeente">
                    <input value={form.city} onChange={set("city")}
                           data-testid="comp-city"
                           placeholder="Willebroek" className={inputCls} />
                  </FormField>
                </div>
              </div>

              <FormField label="Categorie" icon={HomeIcon}>
                <select value={form.category} onChange={set("category")}
                        data-testid="comp-category"
                        className={`${inputCls} appearance-none cursor-pointer`}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </FormField>

              <label className="block mb-3">
                <div className="text-[11px] font-medium text-[var(--ink-700)] uppercase
                                tracking-wider mb-1 flex items-center justify-between gap-1.5">
                  <span className="flex items-center gap-1.5">
                    <MapPin size={11} className="text-[var(--ink-400)]" />
                    Zoekstraal
                  </span>
                  <span className="font-display tabular text-[13px] text-[var(--teal-700)]
                                   normal-case tracking-normal"
                        data-testid="comp-radius-value">
                    {form.max_distance_km} km
                  </span>
                </div>
                <input type="range" min="1" max="25" step="1"
                       value={form.max_distance_km}
                       onChange={(e) => setForm(f => ({ ...f, max_distance_km: Number(e.target.value) }))}
                       data-testid="comp-radius-slider"
                       className="w-full h-2 rounded-full bg-[var(--sand-100)]
                                  appearance-none cursor-pointer accent-[var(--teal-600)]
                                  [&::-webkit-slider-thumb]:appearance-none
                                  [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                  [&::-webkit-slider-thumb]:rounded-full
                                  [&::-webkit-slider-thumb]:bg-[var(--teal-600)]
                                  [&::-webkit-slider-thumb]:shadow
                                  [&::-webkit-slider-thumb]:cursor-pointer
                                  [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                                  [&::-moz-range-thumb]:rounded-full
                                  [&::-moz-range-thumb]:bg-[var(--teal-600)]
                                  [&::-moz-range-thumb]:border-0" />
                <div className="flex justify-between text-[10px] text-[var(--ink-400)] mt-1 tabular">
                  <span>1 km</span><span>8 km</span><span>15 km</span><span>25 km</span>
                </div>
              </label>

              <FormField label="Vraagprijs" icon={Euro}>
                <input value={form.price} onChange={set("price")}
                       data-testid="comp-price"
                       placeholder="350000" type="number" inputMode="numeric"
                       className={inputCls} />
                <p className="text-[10px] text-[var(--ink-400)] mt-1 leading-snug">
                  Panden buiten ±25% van deze prijs vallen weg uit de lijst.
                </p>
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Slaapkamers" icon={BedDouble}>
                  <input value={form.bedrooms} onChange={set("bedrooms")}
                         data-testid="comp-bedrooms"
                         placeholder="3" type="number" inputMode="numeric"
                         className={inputCls} />
                </FormField>
                <FormField label="EPC" icon={Zap}>
                  <select value={form.epc} onChange={set("epc")}
                          data-testid="comp-epc"
                          className={`${inputCls} appearance-none cursor-pointer`}>
                    <option value="">—</option>
                    {EPC_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </FormField>
              </div>

              <FormField label="Bewoonbaar (m²)" icon={Ruler}>
                <input value={form.living_area} onChange={set("living_area")}
                       data-testid="comp-living"
                       placeholder="150" type="number" inputMode="numeric"
                       className={inputCls} />
              </FormField>
              <FormField label="Perceel (m²)" icon={Trees}>
                <input value={form.plot_area} onChange={set("plot_area")}
                       data-testid="comp-plot"
                       placeholder="300" type="number" inputMode="numeric"
                       className={inputCls} />
              </FormField>

              <button type="button" onClick={onSearch}
                      disabled={!isValid || loading}
                      data-testid="comp-search-button"
                      className="btn btn-primary w-full mt-2 h-11
                                 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (<><Hourglass size={15} className="animate-spin-slow" /> Zoeken…</>)
                         : (<><Search size={15} /> Vergelijkbare panden zoeken</>)}
              </button>

              {!isValid && (
                <p className="text-[11px] text-[var(--ink-500)] mt-2 leading-snug">
                  Vul minstens adres, postcode en gemeente in. Andere velden zijn optioneel —
                  een leeg veld telt niet mee in de score.
                </p>
              )}
            </div>
          </section>

          {/* ─────────────── Results ─────────────── */}
          <section className="lg:col-span-8 min-w-0">
            {error && (
              <div className="card flex items-start gap-3 text-[var(--danger)] mb-4"
                   data-testid="comp-error">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <div className="text-[13px]">{error}</div>
              </div>
            )}

            {loading && !result && (
              <div className="card py-16 text-center text-[var(--ink-500)]" data-testid="comp-loading">
                <Hourglass size={20} className="mx-auto mb-3 text-[var(--teal-700)] animate-spin-slow" />
                <div className="text-[14px]">Adres geocoderen en kandidaten ophalen…</div>
                <div className="text-[12px] mt-1">Dit kan een paar seconden duren.</div>
              </div>
            )}

            {result && <Summary result={result} />}
            {result && result.matches.length === 0 && (
              <div className="card text-center py-12 px-6" data-testid="comp-empty">
                <h3 className="font-display text-[20px] mb-2">Geen vergelijkbare panden gevonden</h3>
                <p className="text-[13px] text-[var(--ink-500)] max-w-md mx-auto">
                  Binnen een straal van {result.summary.max_distance_km}&nbsp;km zijn er momenteel
                  geen panden in de geselecteerde categorie. Probeer de straal te vergroten of
                  een andere categorie te kiezen.
                </p>
              </div>
            )}

            {result && result.matches.length > 0 && (
              <ul className="space-y-3" data-testid="comp-matches">
                {result.matches.map((m) => <MatchCard key={m.id} m={m} />)}
              </ul>
            )}

            {!result && !loading && !error && (
              <div className="card text-center py-16 px-6"
                   data-testid="comp-idle">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--teal-50)] text-[var(--teal-700)]
                                grid place-items-center mb-4">
                  <Sparkles size={22} strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-[20px] mb-2">Vul een referentiepand in</h3>
                <p className="text-[13px] text-[var(--ink-500)] max-w-md mx-auto">
                  Vul links de gegevens van het pand in en klik op
                  <em> "Vergelijkbare panden zoeken"</em>. De top-20 verschijnt hier rechts.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── helpers ───────────────────────── */
const inputCls =
  "w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm " +
  "text-[var(--ink-900)] outline-none focus:border-[var(--teal-500)] " +
  "focus:ring-2 focus:ring-[var(--teal-100)] transition-colors";

function FormField({ label, icon: Icon, children }) {
  return (
    <label className="block mb-3">
      <span className="text-[11px] font-medium text-[var(--ink-700)] uppercase
                       tracking-wider mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={11} className="text-[var(--ink-400)]" />}
        {label}
      </span>
      {children}
    </label>
  );
}

function Summary({ result }) {
  const s = result.summary;
  return (
    <div className="card mb-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center"
         data-testid="comp-summary">
      <Stat value={s.candidates_scanned} label="Gescand" />
      <Stat value={s.matches_found} label={`Binnen ${s.max_distance_km} km`} />
      <Stat value={s.strong_matches} label="Sterke matches" sub="≥ 75%" />
      <Stat value={`${s.avg_score}%`} label="Gem. score" />
    </div>
  );
}
function Stat({ value, label, sub }) {
  return (
    <div className="px-2">
      <div className="font-display text-[24px] leading-none text-[var(--ink-900)] tabular">
        {value}
      </div>
      <div className="text-[11px] text-[var(--ink-500)] uppercase tracking-wider mt-1.5">
        {label}
      </div>
      {sub && <div className="text-[10px] text-[var(--ink-400)]">{sub}</div>}
    </div>
  );
}

function scoreBadgeColor(pct) {
  if (pct >= 85) return "bg-[var(--teal-600)] text-white";
  if (pct >= 75) return "bg-[var(--teal-100)] text-[var(--teal-700)] border border-[var(--teal-200)]";
  if (pct >= 60) return "bg-[#FCEBC9] text-[#7C5A1E] border border-[#E5C9A0]";
  return "bg-[var(--sand-100)] text-[var(--ink-700)] border border-[var(--border-soft)]";
}

function MatchCard({ m }) {
  const updated = fmtRelativeDate(m.last_update);
  return (
    <li className="card !p-0 overflow-hidden hover:border-[var(--teal-400)]
                   hover:shadow-[0_8px_24px_-16px_rgba(14,90,94,0.25)] transition-all"
        data-testid={`comp-match-${m.id}`}>
      <div className="flex gap-4 p-4">
        {/* Image */}
        <div className="w-[180px] h-[130px] shrink-0 rounded-lg overflow-hidden
                        bg-[var(--sand-100)] grid place-items-center text-[var(--ink-400)]">
          {m.image_url ? (
            <img src={m.image_url} alt={m.address || ""}
                 className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : <ImageOff size={24} />}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`chip !py-[2px] !px-2 !text-[10px] font-semibold tabular ${scoreBadgeColor(m.score_pct)}`}
                      data-testid={`comp-match-score-${m.id}`}>
                  {m.score_pct}% match
                </span>
                <span className="chip !py-[2px] !px-2 !text-[10px] bg-[var(--teal-50)]
                                 text-[var(--teal-700)] border-[var(--teal-200)]">
                  <MapPin size={10} className="inline -mt-0.5 mr-0.5" />
                  {m.distance_km} km
                </span>
                {updated && (
                  <span className="text-[10px] text-[var(--ink-400)]">
                    bijgewerkt {updated}
                  </span>
                )}
              </div>
              <h3 className="text-[15px] font-semibold text-[var(--ink-900)] truncate">
                {m.address || "Onbekend adres"}
              </h3>
              <div className="text-[12px] text-[var(--ink-500)] truncate">
                {[m.postal_code, m.city].filter(Boolean).join(" ")}
                {m.subcategory && <span className="text-[var(--ink-400)]"> · {m.subcategory}</span>}
              </div>
            </div>
            {m.listing_url && (
              <a href={m.listing_url} target="_blank" rel="noopener noreferrer"
                 data-testid={`comp-match-link-${m.id}`}
                 className="p-1.5 rounded-md text-[var(--ink-500)] hover:bg-[var(--sand-100)]
                            hover:text-[var(--teal-700)]"
                 title="Bekijk advertentie">
                <ExternalLink size={14} />
              </a>
            )}
          </div>

          {/* key facts */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[var(--ink-700)] mb-2">
            <Fact icon={Euro} value={fmtEuro(m.price)} />
            <Fact icon={BedDouble} value={m.bedrooms != null ? `${m.bedrooms} slpk` : "—"} />
            <Fact icon={Ruler} value={`Bewoon. ${fmtM2(m.living_area)}`} />
            {m.plot_area != null && <Fact icon={Trees} value={`Perceel ${fmtM2(m.plot_area)}`} />}
            {m.epc && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-[1px] rounded text-[11px]
                                font-medium ${epcColor(m.epc)}`}>
                <Zap size={10} /> {m.epc}
              </span>
            )}
          </div>

          {/* reasons */}
          {m.reasons?.length > 0 && (
            <div className="text-[11px] text-[var(--ink-500)] leading-relaxed border-t border-[var(--border-soft)] pt-2">
              {m.reasons.join(" · ")}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function Fact({ icon: Icon, value }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <Icon size={11} className="text-[var(--ink-400)]" />
      {value}
    </span>
  );
}
